import { useTranslation } from 'react-i18next';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { tErrorBusiness } from 'src/providers/NotificationProvider/Notification.types';
import {
  lookupError,
  legacyToMetaMap,
  lookupOutboundRefusalByCode,
  notifyPlanPauseRefusal,
} from 'src/utils/ErrorDirectory';
import { tErrorEntry } from 'src/utils/ErrorDirectory.types';
import { useGlobalBannerContext } from 'src/providers/GlobalBannerProvider/GlobalBanner.context';

// BUG-820: wba-10026 is whatsapp-business-service's catch-all wire code. Its
// `message` is a fixed constant ("An unexpected error occurred. Please try
// again.") that never says anything, and `detail` (Meta's error_data.details /
// error_user_msg) carries the only text that names the actual problem — e.g.
// "All carousel cards should have at least one button". For this code, and only
// this code, `detail` outranks `message`; every other unregistered code keeps
// the message-first order INT-1103 established deliberately.
const OPAQUE_MESSAGE_CODES = new Set(['wba-10026']);

export function useErrorHandler() {
  const { addAlert } = useToastContext();
  const { t } = useTranslation('errors/errors');
  const { showBanner } = useGlobalBannerContext();

  function handleError(error: unknown, fallback?: string): tErrorEntry | null {
    const errorMeta = (error as tErrorBusiness)?.response?.data;
    const rawCode = errorMeta?.code ?? errorMeta?.error;
    // INT-1103: prefer the backend's explicit `message` when the code isn't
    // registered in errorDirectory. Backends like whatsapp-business-service
    // return localized copy (e.g. wba-flows-unbound-button from INT-1098);
    // discarding it and showing the caller's generic fallback strips the
    // actionable text the user needs. tErrorBusiness declares message as
    // required, but the runtime is unknown — treat it as optional.
    const rawServerMessage: unknown = errorMeta?.message;
    const serverMessage =
      typeof rawServerMessage === 'string' && rawServerMessage.trim() !== ''
        ? rawServerMessage
        : undefined;

    // BUG-820: Meta can answer with an empty `detail`, so sanitize it the same
    // way as `message` before letting it win anywhere.
    const rawServerDetail: unknown = errorMeta?.detail;
    const serverDetail =
      typeof rawServerDetail === 'string' && rawServerDetail.trim() !== ''
        ? rawServerDetail
        : undefined;

    // BUG-820: unmigrated WBS controllers answer 400s with `{"error": "<full
    // sentence>"}` (TemplateController.go — "BODY component is required",
    // "cards[2].header: ..."). The rawCode line above takes that whole sentence
    // as a lookup key, it never resolves, and the caller's generic fallback
    // wins — so the per-card feedback INT-1279 built has never been seen. Tell
    // prose apart from `{"error": "wba-10026"}` — a *code* under the same key,
    // which is what metaErrorToJSON emits — by requiring whitespace: every
    // prose message is a sentence, no wire code has a space in it. Trim first,
    // or a run of spaces passes the /\s/ test and renders a blank toast, and a
    // padded wire code reads as prose.
    //
    // Caveat inherited from lookupError: it runs parseInt on the key, so a
    // sentence starting with a bare 200-299 integer ("250 characters exceeded")
    // resolves to the PERMISSION_RANGE sentinel and never reaches this branch.
    // No known WBS sentence starts that way today. If one appears, that lookup
    // (ErrorDirectory.ts) is where it dies, not here.
    const trimmedError =
      typeof errorMeta?.error === 'string' ? errorMeta.error.trim() : '';
    const proseError =
      errorMeta?.code === undefined &&
      trimmedError !== '' &&
      /\s/.test(trimmedError)
        ? trimmedError
        : undefined;

    if (!rawCode) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          serverMessage ??
          fallback ??
          (error instanceof Error ? error.message : t('wapi.unexpectedError')),
      });
      return null;
    }

    let code: string | number = rawCode;

    // Legacy wba- code support
    if (typeof code === 'string' && code.startsWith('wba-')) {
      const mapped = legacyToMetaMap[code];
      if (mapped !== undefined) {
        code = mapped;
      }
    }

    const entry = lookupError(code);
    if (lookupOutboundRefusalByCode(rawCode)?.kind === 'paused') {
      notifyPlanPauseRefusal();
    }

    // Checked against `rawCode`, not `errorMeta.code`: WBS sends wba-10026 under
    // `code` on the create path (WriteMappedError) and under `error` on the read
    // paths (metaErrorToJSON), and `rawCode` already coalesces both. wba-10026 is
    // absent from legacyToMetaMap, so `code` is never remapped away from it.
    const prefersDetail =
      typeof rawCode === 'string' && OPAQUE_MESSAGE_CODES.has(rawCode);

    if (!entry) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (prefersDetail ? serverDetail : undefined) ??
          serverMessage ??
          proseError ??
          fallback ??
          String(code),
      });
      return null;
    }

    switch (entry.displayMethod) {
      case 'toast':
        addAlert({
          type: eAlertType.WARNING,
          title: t(entry.titleKey),
          message: t(entry.messageKey),
        });
        break;
      case 'banner':
        showBanner({
          code: entry.code,
          titleKey: entry.titleKey,
          messageKey: entry.messageKey,
          modules: entry.modules,
        });
        break;
    }

    // Return entry so consumer can handle chat_inline/input_block/template_button
    return entry;
  }

  return { handleError };
}
